import { t } from '@lingui/macro'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { mixpanel } from '@/mixpanel'
import { CareQuestionDetailContentLayout } from '@/modules/care-question/components/care-question-detail-content-layout/CareQuestionDetailContentLayout'

import type { FC } from 'react'

type Category = {
  title: string
  navigateTo: string
  trackCategory: string
}
const categories: Category[] = [
  {
    title: t({
      id: 'care-question.care-question.category.medical-question',
      message: 'Medische vraag',
    }),
    navigateTo: '/care-question/medical-question',
    trackCategory: 'medical-question',
  },
  // {
  //   title: t({
  //     id: 'care-question.care-question.category.administrative-question',
  //     message: 'Administratieve vraag',
  //   }),
  //   navigateTo: '/care-question/coming-soon',
  //   trackCategory: 'administrative-question',
  // },
  {
    title: t({
      id: 'care-question.care-question.category.app-question',
      message: 'Vraag over de app',
    }),
    navigateTo: '/care-question/app-question',
    trackCategory: 'app-question',
  },
]

export const TypeSelectPage: FC = () => {
  const navigate = useNavigate()
  const theme = useTheme()

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)

  useHideNativeNavigation()

  // Event Handlers
  const handleNavigation = () => {
    if (!selectedCategory) return

    mixpanel.track('care_question_category_selected', {
      category: selectedCategory.trackCategory,
    })
    navigate(selectedCategory.navigateTo)
  }

  // Render
  return (
    <CareQuestionDetailContentLayout
      header={{
        secondaryAction: {
          icon: ['far', 'close'],
          label: t({
            id: 'globals.close',
          }),
          onClick: () => navigate(-1),
        },
      }}
      bottom={
        <Button variant='Primary' onClick={handleNavigation} disabled={!selectedCategory}>
          {t({
            id: 'global.continue',
            message: 'Ga verder',
          })}
        </Button>
      }
    >
      <StContentContainer gap={theme.UI.Spacing.Space8}>
        {/* TITLE */}
        <FlexColumn gap={theme.UI.Spacing.Space4}>
          <H3 noMargin>
            {t({
              id: 'care-question.question-flow.title',
              message: 'Ik heb een vraag',
            })}
          </H3>
          <P type='BodyMediumRegular' style={{ fontWeight: 400 }}>
            {t({
              id: 'care-question.question-flow.category.description',
              message: 'Wat is de aard van je vraag?',
            })}
          </P>
        </FlexColumn>

        {/* CATEGORIES */}
        <FlexColumn gap={theme.UI.Spacing.Space3}>
          {categories.map((category, index) => {
            const { title } = category
            const isSelected = selectedCategory?.trackCategory === category.trackCategory

            return (
              <StQuickReplyContainer key={index} selected={isSelected} onClick={() => setSelectedCategory(category)}>
                <P className='category-title' type='BodyLargeRegular'>
                  {title}
                </P>
              </StQuickReplyContainer>
            )
          })}
        </FlexColumn>
      </StContentContainer>
    </CareQuestionDetailContentLayout>
  )
}

const StContentContainer = styled(FlexColumn)`
  justify-content: flex-end;
  flex-grow: 1;

  @media (${breakpoints.fromS}) {
    justify-content: flex-start;
  }
`

const StQuickReplyContainer = styled.div<{ selected?: boolean }>`
  border: 1px solid
    ${({ theme, selected }) =>
      selected ? theme.Components.Conversational.ActiveBorder : theme.Components.Conversational.Border};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
  background-color: ${({ theme, selected }) =>
    selected ? theme.Components.Conversational.ActiveFill : theme.Components.Conversational.Fill};

  padding: ${({ theme }) => theme.UI.SpacingPx.Space3}
    ${({ theme }) => theme.UI.SpacingPx.Space4};

  cursor: pointer;

  p {
    margin: 0;
    font-weight: 400;
  }

  .category-title {
    color: ${({ theme, selected }) =>
      selected ? theme.Components.Conversational.ActiveText : theme.Components.Conversational.Text};
  }

  &:active {
    border-color: ${({ theme }) => theme.Components.Conversational.ActiveBorder};
  }
`
